import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AutoScrollToErrorField, Button, ServerForm, StickyActionBar } from '@uplift/ui-v2';
import { Form, useFormikContext } from 'formik';
import React from 'react';
import { ClaimCorrectionFormType, Diagnosis, ProviderMedicalRole } from '@uplift/types';
import { NoteSection } from '../notes-page/components/NoteSection';
import { MedicalNoteForm } from '@uplift/api';
import { AmendCptCodeSection } from './components/amend-cpt-codes.component';
import { AmendDiagnosisSection } from './components/amend-diagnosis.component';
import { AmendOtherSection } from './components/amend-other.component';

export type ClaimCorrectionFormProps = {
  claimCorrectionForm: MedicalNoteForm;
  diagnoses: Diagnosis[];
  providerId: string;
  medicalRole: ProviderMedicalRole;
  handleDelete: () => void;
};

export const ClaimCorrectionForm: React.VFC<ClaimCorrectionFormProps> = ({
  providerId,
  medicalRole,
  claimCorrectionForm,
  diagnoses,
  handleDelete
}) => {
  const { classes } = useStyles();
  const formik = useFormikContext<ClaimCorrectionFormType>();

  const details = claimCorrectionForm.fields.filter(x => x.id === 'service_date' || x.id === 'code_change_date' );
  const otherReason = claimCorrectionForm.fields.filter(x => x.id === 'other_code_change_reason');
  const otherDescription = claimCorrectionForm.fields.filter(x => x.id === 'code_change_description');
  const cptCodeChangeReason = claimCorrectionForm.fields.filter(x => x.id === 'cpt_code_change_reason');
  const diagnosisCodeChangeReason = claimCorrectionForm.fields.filter(x => x.id === 'diagnosis_code_change_reason');
  const buttonGroup = claimCorrectionForm.fields.filter(x => x.fieldType === 'boolean');

  return (
    <Form>
      <AutoScrollToErrorField />
      <Box className={classes.content}>
        <Typography variant="h3" className={classes.title}>
          Claim Corrections
        </Typography>
        <Box pb={3}>
          <NoteSection title="Details">
            <ServerForm fields={details} namespace="claim" />
          </NoteSection>
        </Box>
        <Box className={classes.amendFields}>
          <AmendCptCodeSection
            open={!!formik.values.claim['cpt_field_toggle']}
            providerId={providerId}
            medicalRole={medicalRole}
            cptCodeChangeReason={cptCodeChangeReason}
          />
          <AmendDiagnosisSection
            open={!!formik.values.claim['diagnosis_field_toggle']}
            diagnoses={diagnoses}
            diagnosisCodeChangeReason={diagnosisCodeChangeReason}
          />
          <AmendOtherSection
            open={!!formik.values.claim['other_field_toggle']}
            reason={otherReason}
            description={otherDescription}
          />
          <ServerForm fields={buttonGroup} namespace="claim" />
        </Box>
      </Box>
      <StickyActionBar
        leftContainer={(
          <Button
            variant="outlined"
            color="secondary"
            disabled={formik.isSubmitting}
            onClick={handleDelete}
          >
            Delete Note
          </Button>
        )}
      >
        <Button
          variant="contained"
          color="secondary"
          disabled={formik.isSubmitting}
          onClick={formik.handleSubmit}
        >
          Sign and submit
        </Button>
      </StickyActionBar>
    </Form>
  );
};

const useStyles = makeStyles({ name: 'ClaimCorrectionsPage' })(theme => ({
  content: {
    marginLeft: theme.spacing(15),
    marginTop: theme.spacing(8),
    paddingBottom: theme.spacing(15),
    width: '792px',
  },
  title: {
    marginBottom: theme.spacing(8),
  },
  amendFields: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    gridGap: '24px'
  },
  button: {
    justifyContent: 'flex-start',
  },
}));
